// React
import { useState } from "react";
import { useTranslation } from "react-i18next";

// Amplify
import { resetPassword, confirmResetPassword } from "aws-amplify/auth";

// Redux
import { useDispatch, useSelector } from "react-redux";
import { setAuthState, setUserEmail, selectUserEmail } from "Store/AuthSlice";
import { openSnackbar } from "Store/SnackbarSlice";

// Material UI
import {
  Box,
  Typography,
  Button,
  Stack,
  InputAdornment,
  CircularProgress,
} from "@mui/material";
import {
  MailOutline,
  LockOutlined,
  ChevronLeft,
  Https,
} from "@mui/icons-material";

// Components
import { OTP } from "../Components/OTP";
import PasswordField from "Components/PasswordField";
import { FormikTextField } from "Common/Components";

// Formik
import { Formik } from "formik";

// Styles
import { styles } from "./styles";
import { boxStyles } from "Common/Styles/BoxLayouts";
import ConfirmSignUp from "../ConfirmSignUp";

// Validation
import {
  emailValidationSchema,
  passwordValidationSchema,
} from "Validation/UserValidation";

const ResetPassword = () => {
  const { t } = useTranslation();
  const userEmail = useSelector(selectUserEmail);
  const dispatch = useDispatch();
  const [confirmSignupOpen, setConfirmSignupOpen] = useState(false);
  const prefilledEmail = userEmail?.trim() || "";
  const [isCodeStep, setIsCodeStep] = useState(Boolean(prefilledEmail));

  const handleForgotPassword = async (values) => {
    const { email } = values;

    try {
      const { nextStep } = await resetPassword({ username: email });
      switch (nextStep.resetPasswordStep) {
        case "CONFIRM_RESET_PASSWORD_WITH_CODE":
          setIsCodeStep(true);
          dispatch(setUserEmail(email));
          dispatch(
            openSnackbar({
              message: t("resetPassword.verificationSent"),
              severity: "success",
            })
          );
          break;
        default:
          break;
      }
    } catch (error) {
      let errorMessage;
      switch (error.name) {
        case "UserNotFoundException":
          errorMessage = t("errors.userNotFoundReset");
          break;
        case "InvalidParameterException":
          dispatch(setUserEmail(email));
          setConfirmSignupOpen(true);
          return;
        case "LimitExceededException":
          errorMessage = t("errors.tooManyAttempts");
          break;
        default:
          errorMessage = error.message;
          break;
      }

      dispatch(openSnackbar({ message: errorMessage, severity: "error" }));
    }
  };

  const handleForgotPasswordSubmit = async (values) => {
    const { code, password } = values;

    try {
      await confirmResetPassword({
        username: userEmail,
        confirmationCode: code,
        newPassword: password,
      });
      dispatch(
        openSnackbar({
          message: t("resetPassword.passwordResetSuccess"),
          severity: "success",
        })
      );
      dispatch(setUserEmail(null));
      dispatch(setAuthState("SignIn"));
    } catch (error) {
      let errorMessage;
      switch (error.name) {
        case "CodeMismatchException":
          errorMessage = t("errors.invalidCode");
          break;
        case "LimitExceededException":
          errorMessage = t("errors.attemptLimitExceeded");
          break;
        case "PasswordHistoryPolicyViolationException":
          errorMessage = t("errors.previouslyUsed");
          break;
        default:
          errorMessage = error.message;
          break;
      }

      dispatch(openSnackbar({ message: errorMessage, severity: "error" }));
    }
  };

  const handleBack = () => {
    dispatch(setUserEmail(null));
    dispatch(setAuthState("SignIn"));
  };

  const ActionButtons = ({ buttonTitle, loading, onSubmit }) => {
    return (
      <Box sx={boxStyles.verticalButtonGroupBox}>
        <Button
          type="button"
          variant="contained"
          fullWidth
          disabled={loading}
          startIcon={
            loading ? <CircularProgress size={18} color="inherit" /> : null
          }
          onClick={onSubmit}
        >
          {t(`resetPassword.${buttonTitle}`)}
        </Button>
        <Button
          type="button"
          variant="text"
          fullWidth
          disabled={loading}
          startIcon={<ChevronLeft color={loading ? "disabled" : "primary"} />}
          onClick={handleBack}
        >
          {t("buttons.back")}
        </Button>
      </Box>
    );
  };

  return (
    <Stack sx={styles.resetPasswordContainer}>
      <Stack sx={styles.contentBox}>
        <Formik
          initialValues={{ email: prefilledEmail }}
          enableReinitialize
          validationSchema={emailValidationSchema(t)}
          onSubmit={handleForgotPassword}
        >
          {(formik) => (
            <Stack spacing={4} component="form" onSubmit={formik.handleSubmit}>
              <Stack spacing={2}>
                <Typography sx={styles.title}>
                  {t("resetPassword.title")}
                </Typography>
                <FormikTextField
                  formik={formik}
                  id="email"
                  name="email"
                  field={{
                    type: "email",
                    label: t("common.email"),
                    required: false,
                    disabled: isCodeStep,
                    autoComplete: "email",
                    slotProps: {
                      input: {
                        [isCodeStep ? "endAdornment" : "startAdornment"]: (
                          <InputAdornment
                            position={isCodeStep ? "end" : "start"}
                          >
                            {isCodeStep ? <Https /> : <MailOutline />}
                          </InputAdornment>
                        ),
                      },
                    },
                    helperText: isCodeStep
                      ? t("mfa.verificationEmailSent")
                      : "",
                  }}
                />
              </Stack>
              {!isCodeStep && (
                <ActionButtons
                  buttonTitle="sendCode"
                  loading={formik.isSubmitting}
                  onSubmit={formik.handleSubmit}
                />
              )}
            </Stack>
          )}
        </Formik>
        {isCodeStep && (
          <Formik
            initialValues={{ code: "", password: "", confirmPassword: "" }}
            validationSchema={passwordValidationSchema(t)}
            onSubmit={handleForgotPasswordSubmit}
          >
            {(formik) => (
              <Stack
                spacing={4}
                component="form"
                onSubmit={formik.handleSubmit}
              >
                <Stack spacing={2}>
                  <Typography sx={styles.subtitle}>
                    {t("mfa.verificationCode")}
                  </Typography>
                  <OTP
                    setCode={formik.setFieldValue}
                    resendCode={() =>
                      handleForgotPassword({ email: userEmail })
                    }
                    values={formik.values}
                    handleChange={formik.handleChange("code")}
                    errors={formik.errors}
                    touched={formik.touched}
                  />
                </Stack>
                <Stack spacing={2}>
                  <Typography sx={styles.subtitle}>
                    {t("resetPassword.enterNewPassword")}
                  </Typography>
                  <PasswordField
                    formik={formik}
                    name="password"
                    label={t("common.newPassword")}
                    autoComplete="new-password"
                  />
                  <FormikTextField
                    formik={formik}
                    name="confirmPassword"
                    field={{
                      type: "password",
                      label: t("common.confirmNewPassword"),
                      required: false,
                      autoComplete: "new-password",
                      slotProps: {
                        input: {
                          startAdornment: (
                            <InputAdornment position="start">
                              <LockOutlined />
                            </InputAdornment>
                          ),
                        },
                      },
                    }}
                  />
                </Stack>
                <ActionButtons
                  buttonTitle="resetPasswordButton"
                  loading={formik.isSubmitting}
                  onSubmit={formik.handleSubmit}
                />
              </Stack>
            )}
          </Formik>
        )}
      </Stack>

      <ConfirmSignUp
        open={confirmSignupOpen}
        onClose={() => {
          setConfirmSignupOpen(false);
          setIsCodeStep(true);
          handleForgotPassword({ email: userEmail });
          dispatch(
            openSnackbar({
              message: t("resetPassword.accountVerified"),
              severity: "success",
            })
          );
        }}
      />
    </Stack>
  );
};

export default ResetPassword;
